import React, { useEffect, useState } from 'react';
import { authApiClient } from '../../services/authApi';

// Admin "Sales Tone Fixer" — staging-only tool; the backend gates it behind
// SALES_TONE_FIXER_ENABLED and the config endpoint reports whether it is on.

interface Config {
  enabled: boolean;
  default_prompt: string;
  models: string[];
  default_model: string;
}

const modelLabel = (m: string) => (m.endsWith('sol') ? 'Sol' : m.endsWith('terra') ? 'Terra' : m);

export const SalesToneFixerTab: React.FC = () => {
  const [config, setConfig] = useState<Config | null>(null);
  const [format, setFormat] = useState<'linkedin' | 'email'>('linkedin');
  const [target, setTarget] = useState('');
  const [role, setRole] = useState('');
  const [company, setCompany] = useState('');
  const [history, setHistory] = useState('');
  const [model, setModel] = useState('');
  const [prompt, setPrompt] = useState('');
  const [showPrompt, setShowPrompt] = useState(false);
  const [output, setOutput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    authApiClient.get('/admin/sales-tone/config')
      .then((res) => {
        if (cancelled) return;
        setConfig(res.data);
        setModel(res.data.default_model);
        setPrompt(res.data.default_prompt);
      })
      .catch((err: any) => {
        if (!cancelled) setError((typeof err?.response?.data?.detail === 'string' && err.response.data.detail) || err?.message || 'Failed to load config');
      });
    return () => { cancelled = true; };
  }, []);

  const rewrite = async () => {
    if (!config) return;
    setLoading(true);
    setError(null);
    try {
      const res = await authApiClient.post('/admin/sales-tone/rewrite', {
        format,
        target: target.trim(),
        role: role.trim(),
        company: company.trim(),
        conversation_history: history,
        model,
        ...(prompt !== config.default_prompt ? { prompt } : {}),
      });
      setOutput(res.data.text);
    } catch (err: any) {
      setError((typeof err?.response?.data?.detail === 'string' && err.response.data.detail) || err?.message || 'Draft failed');
    } finally {
      setLoading(false);
    }
  };

  const noMessage = output.startsWith('NO_MESSAGE:');
  const copy = () => { navigator.clipboard?.writeText(output).catch(() => undefined); };

  if (!config) {
    return error
      ? <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>
      : <div className="text-sm text-slate-500">Loading…</div>;
  }
  if (!config.enabled) {
    return <div className="text-sm text-slate-500">Sales Tone Fixer is not enabled in this environment.</div>;
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <div>
          <label htmlFor="sales-tone-format" className="block text-slate-500 mb-1">Format</label>
          <select id="sales-tone-format" value={format}
            onChange={(e) => setFormat(e.target.value as 'linkedin' | 'email')}
            className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm bg-white">
            <option value="linkedin">LinkedIn</option>
            <option value="email">Email</option>
          </select>
        </div>
        {([['target', 'Target', target, setTarget], ['role', 'Role', role, setRole],
           ['company', 'Company', company, setCompany]] as const).map(([id, label, value, set]) => (
          <div key={id}>
            <label htmlFor={`sales-tone-${id}`} className="block text-slate-500 mb-1">{label}</label>
            <input id={`sales-tone-${id}`} type="text" value={value} maxLength={200}
              onChange={(e) => set(e.target.value)}
              className="w-full border border-slate-300 rounded px-3 py-1.5 text-sm bg-white" />
          </div>
        ))}
      </div>
      <div>
        <label htmlFor="sales-tone-history" className="block text-sm text-slate-500 mb-1">Conversation history</label>
        <textarea id="sales-tone-history" value={history} onChange={(e) => setHistory(e.target.value)}
          rows={8} maxLength={20000}
          placeholder="Leave blank for the first message. Paste prior messages, oldest first."
          className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-white" />
      </div>

      <div className="flex items-center gap-3 text-sm">
        <label htmlFor="sales-tone-model" className="text-slate-500">Model</label>
        <select id="sales-tone-model" value={model} onChange={(e) => setModel(e.target.value)}
          className="border border-slate-300 rounded px-2 py-1 text-sm bg-white">
          {config.models.map((m) => <option key={m} value={m}>{modelLabel(m)}</option>)}
        </select>
        <button onClick={() => setShowPrompt(!showPrompt)}
          className="px-3 py-1 border border-slate-300 rounded text-xs font-medium hover:bg-slate-50">
          {showPrompt ? 'Hide prompt' : 'Prompt'}
        </button>
      </div>

      {showPrompt && (
        <div>
          <textarea aria-label="Prompt" value={prompt} onChange={(e) => setPrompt(e.target.value)}
            rows={10} maxLength={20000}
            className="w-full border border-slate-300 rounded px-3 py-2 text-xs font-mono bg-white" />
          <button onClick={() => setPrompt(config.default_prompt)}
            className="mt-1 px-3 py-1 border border-slate-300 rounded text-xs font-medium hover:bg-slate-50">
            Reset to default
          </button>
        </div>
      )}

      <button onClick={rewrite} disabled={loading || !target.trim() || !role.trim() || !company.trim()}
        className="px-4 py-2 bg-slate-800 text-white rounded text-sm font-medium disabled:opacity-50">
        {loading ? 'Drafting…' : 'Draft message'}
      </button>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">{error}</div>
      )}

      {output && noMessage && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
          <div className="font-medium">Suggested: don't send a message.</div>
          <div>{output.slice('NO_MESSAGE:'.length).trim()}</div>
        </div>
      )}

      {output && !noMessage && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <label htmlFor="sales-tone-output" className="text-sm text-slate-500">Message</label>
            <button onClick={copy}
              className="px-3 py-1 border border-slate-300 rounded text-xs font-medium hover:bg-slate-50">
              Copy
            </button>
          </div>
          <textarea id="sales-tone-output" readOnly value={output} rows={8}
            className="w-full border border-slate-300 rounded px-3 py-2 text-sm bg-slate-50" />
        </div>
      )}
    </div>
  );
};
